import React, { useState } from 'react';
import { COLOR_SCHEME } from '../../../styling/ColorSchemes';

export interface ServiceCardProps {
  service: {
    id: string;
    name: string;
    description: string;
    durationMinutes: number;
    price: number;
    currency: string;
  };
  onSelect?: () => void;
  hideButton?: boolean;
  colorScheme: COLOR_SCHEME;
  selected?: boolean;
  compact?: boolean;
  screenWidth?: number;
}

const formatPrice = (currency: string, amount: number) => `${currency} $${amount.toFixed(2)}`;

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  onSelect,
  hideButton,
  colorScheme,
  selected = false,
  compact = false,
  screenWidth = 768,
}) => {
  const isMobile = screenWidth < 768;
  const isSuperTiny = screenWidth <= 320;
  const layoutScale = Math.min(1, Math.max(0, (screenWidth - 280) / 600));
  const scaledRem = (minimum: number, maximum: number) =>
    `${minimum + (maximum - minimum) * layoutScale}rem`;
  const maximumPadding = compact ? 0.9 : 1.15;
  const maximumGap = compact ? 0.7 : 0.85;
  const maximumBadgeSize = compact ? 2.3 : 2.75;
  const [isExpanded, setIsExpanded] = useState(false);
  const isClickable = Boolean(onSelect) && !hideButton;

  const rootStyle: React.CSSProperties = {
    background: '#FFFFFF',
    border: `1.5px solid ${selected ? colorScheme.PRIMARY : '#DED8F6'}`,
    borderRadius: '1.15rem',
    padding: scaledRem(0.6, maximumPadding),
    boxShadow: selected
      ? `0 0.85rem 2rem ${colorScheme.PRIMARY}22`
      : '0 0.55rem 1.4rem rgba(15, 23, 42, 0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: scaledRem(0.45, maximumGap),
    textAlign: 'left',
    cursor: isClickable && !isSuperTiny ? 'pointer' : 'default',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    minHeight: hideButton ? undefined : (isMobile ? 'auto' : compact ? '188px' : '220px'),
    position: 'relative',
    overflow: 'hidden',
  };

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: scaledRem(2, maximumBadgeSize),
    height: scaledRem(2, maximumBadgeSize),
    borderRadius: scaledRem(0.65, 0.85),
    background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT || colorScheme.PRIMARY} 100%)`,
    color: '#FFFFFF',
    fontSize: scaledRem(0.75, compact ? 0.9 : 1),
    fontWeight: 800,
    boxShadow: `0 0.5rem 1rem ${colorScheme.PRIMARY}30`,
    flexShrink: 0,
  };

  const handleActivate = () => {
    if (isClickable && onSelect) {
      onSelect();
    }
  };

  return (
    <div
      style={rootStyle}
      onClick={isSuperTiny ? undefined : handleActivate}
      onKeyDown={(event) => {
        if (!isSuperTiny && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          handleActivate();
        }
      }}
      role={isClickable && !isSuperTiny ? 'button' : undefined}
      tabIndex={isClickable && !isSuperTiny ? 0 : undefined}
      aria-pressed={isClickable && !isSuperTiny ? selected : undefined}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(160deg, ${colorScheme.SECONDARY || '#F8F5FF'} 0%, transparent 38%, ${colorScheme.ACCENT || colorScheme.PRIMARY}12 100%)`,
          pointerEvents: 'none',
          opacity: selected ? 1 : 0.65,
        }}
      />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: scaledRem(0.5, compact ? 0.7 : 0.85) }}>
        <div style={badgeStyle}>
          {service.name.trim().charAt(0).toUpperCase() || 'S'}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: scaledRem(0.85, compact ? 0.95 : 1.05), fontWeight: 800, color: '#1F2937', lineHeight: 1.25 }}>
            {service.name}
          </div>
          <div style={{ marginTop: scaledRem(0.25, 0.35), display: 'flex', flexWrap: 'wrap', gap: scaledRem(0.3, 0.45) }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '999px',
                background: '#F6F1FF',
                color: colorScheme.PRIMARY,
                padding: `${scaledRem(0.2, 0.28)} ${scaledRem(0.4, 0.58)}`,
                fontSize: scaledRem(0.6, 0.68),
                fontWeight: 700,
              }}
            >
              {service.durationMinutes} min
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                borderRadius: '999px',
                background: selected ? `${colorScheme.PRIMARY}14` : '#FFF7ED',
                color: selected ? colorScheme.PRIMARY : '#B45309',
                padding: `${scaledRem(0.2, 0.28)} ${scaledRem(0.4, 0.58)}`,
                fontSize: scaledRem(0.6, 0.68),
                fontWeight: 800,
              }}
            >
              {formatPrice(service.currency, service.price)}
            </span>
          </div>
        </div>
      </div>

      {/* Description Section - Collapsible on super tiny screens */}
      {isSuperTiny ? (
        <div style={{ position: 'relative' }}>
          <div
            style={{
              fontSize: '0.68rem',
              lineHeight: 1.55,
              color: '#4B5563',
              maxHeight: isExpanded ? 'none' : '1.2rem',
              overflow: isExpanded ? 'visible' : 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: isExpanded ? 'unset' : 1,
              transition: 'max-height 0.3s ease',
            }}
          >
            {service.description || 'A polished service experience tailored to your booking needs.'}
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            style={{
              marginTop: '0.5rem',
              background: 'none',
              border: 'none',
              color: colorScheme.PRIMARY,
              fontSize: '0.6rem',
              fontWeight: 700,
              cursor: 'pointer',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {isExpanded ? 'Show less' : 'See more'}
          </button>
        </div>
      ) : (
        <div
          style={{
            position: 'relative',
            fontSize: scaledRem(0.68, compact ? 0.75 : 0.8),
            lineHeight: 1.55,
            color: '#4B5563',
            minHeight: isMobile ? undefined : compact ? '3rem' : '3.8rem',
          }}
        >
          {service.description || 'A polished service experience tailored to your booking needs.'}
        </div>
      )}

      {!hideButton ? (
        <div
          style={{
            position: 'relative',
            marginTop: isSuperTiny ? 'auto' : 'auto',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          {!isSuperTiny && (
            <div style={{ fontSize: scaledRem(0.6, 0.7), color: '#6B7280', fontWeight: 600 }}>
              {selected ? 'Selected' : 'Tap anywhere to continue'}
            </div>
          )}
          {isSuperTiny && isClickable && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                handleActivate();
              }}
              style={{
                width: '100%',
                border: 'none',
                borderRadius: '0.55rem',
                padding: '0.5rem 0.7rem',
                background: colorScheme.PRIMARY,
                color: '#FFFFFF',
                fontSize: '0.68rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Select service
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ServiceCard;
